import { TutorEntry } from "../game/controller";
import { applyPlay } from "../engine/moves";
import { Position, hashPosition } from "../engine/position";
import { buildHintTable, HintRow } from "./hint-table";
import { iconMarkup } from "./icons";
import { installModalBehavior, installPeek } from "./modal";
import { rankDistinctPlays, RankedPlay } from "./ranked-plays";
import { describePlay } from "./tutor-panel";

export interface TutorModalOpts {
  setTutorPreview: (
    pre: { startPos: Position; side: 0 | 1; hintMoves: { from: number; to: number }[] | null } | null,
  ) => void;
  onDismiss: () => void;
}

export function openTutorModal(
  entry: TutorEntry,
  overlayContainer: HTMLElement,
  opts: TutorModalOpts,
): void {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop tutor-modal-backdrop board-anchored";
  const modal = document.createElement("div");
  modal.className = `modal tutor-analysis-modal ${entry.classification}`;
  const head = entry.classification === "blunder" ? "Blunder" : "Error";
  modal.innerHTML = `
    <div class="modal-head">
      <h2 class="tutor-head">${head}</h2>
      <button class="peek-btn" data-action="peek" type="button"
              title="Hold to see the board" aria-label="Hold to see the board">${iconMarkup("peek")}</button>
    </div>
    <p class="tutor-summary"></p>
    <div class="hint-body"></div>
    <div class="actions">
      <button data-action="ok" class="primary">OK</button>
    </div>
  `;
  backdrop.appendChild(modal);
  overlayContainer.appendChild(backdrop);

  // Rank plays by points equity desc (the same metric entry.bestPlay and
  // equityLoss are measured in), deduped by final position. Best = #1,
  // second-best = #2, etc.
  const ranked = rankDistinctPlays(
    entry.startPos,
    entry.legalPlays,
    entry.pointsEquities,
    entry.gameEquities,
  );
  const yourFinalHash = hashPosition(applyPlay(entry.startPos, entry.yourPlay));
  const yourIdx = ranked.findIndex((r) => r.finalHash === yourFinalHash);

  // Keep the user's move in its true ranked context. Inside the top 5 it just
  // sits at its own number. Worse than #5, drop the #5 row and put the user's
  // move in that slot still carrying its real rank — the list reads
  // #1 #2 #3 #4 #14, and the jump itself says how far down they were. (The
  // old behavior hoisted an unnumbered copy to the top, which showed *that*
  // you were wrong but never *how* wrong.)
  const ROWS = 5;
  const shown: RankedPlay[] =
    yourIdx >= 0 && yourIdx < ROWS
      ? ranked.slice(0, ROWS)
      : yourIdx >= 0
        ? [...ranked.slice(0, ROWS - 1), ranked[yourIdx]]
        : ranked.slice(0, ROWS - 1);

  const renderedRows: HintRow[] = shown.map((r) => {
    const isYours = r.finalHash === yourFinalHash;
    return {
      // Show the user's own sub-move ordering rather than the canonical one
      // that happened to win the dedup for this final position.
      play: isYours ? entry.yourPlay : r.play,
      equity: r.equity,
      winProb: r.winProb,
      rank: r.rank,
      isBest: r.rank === 1,
      isYours,
    };
  });
  // Defensive: the played move should always be among the legal plays. If it
  // somehow isn't, still show it — as dashes, with a blank rank cell.
  if (yourIdx < 0) {
    renderedRows.push({
      play: entry.yourPlay,
      equity: -Infinity,
      winProb: undefined,
      rank: -1,
      isBest: false,
      isYours: true,
    });
  }
  const bestEquity = ranked.length > 0 ? ranked[0].equity : 0;

  // One plain-language sentence before the numbers — the numbers alone don't
  // teach. "Best" is the table's own #1 row, which equals entry.bestPlay's
  // final position by the AnalysisResult invariant (one metric end to end);
  // using the table's own row keeps the sentence and the list agreeing even
  // if that invariant ever regresses. describePlay emits sanitized notation
  // only.
  const tableBest = ranked.length > 0 ? ranked[0].play : entry.bestPlay;
  modal.querySelector<HTMLElement>(".tutor-summary")!.textContent =
    `${describePlay(entry.yourPlay)} gave up ${entry.equityLoss.toFixed(3)} equity — ` +
    `best was ${describePlay(tableBest)}.`;

  const body = modal.querySelector<HTMLElement>(".hint-body")!;
  const table = buildHintTable({
    rows: renderedRows,
    bestEquity,
    onSelect: (row) => {
      opts.setTutorPreview({
        startPos: entry.startPos,
        side: entry.side as 0 | 1,
        hintMoves: row.play.map((sm) => ({ from: sm.from, to: sm.to })),
      });
    },
    // Open on the *best* move, not the user's — the board should show the
    // correction, and their own move is one tap away. Rows are rank-ordered
    // so this is 0 in practice; deriving it keeps the intent explicit.
    defaultSelectIdx: Math.max(0, renderedRows.findIndex((r) => r.isBest)),
  });
  body.appendChild(table);

  let dismissed = false;
  const close = (): void => {
    if (dismissed) return;
    dismissed = true;
    opts.setTutorPreview(null);
    backdrop.remove();
    opts.onDismiss();
  };
  modal.querySelector<HTMLButtonElement>('[data-action="ok"]')!.addEventListener("click", close);
  installModalBehavior(backdrop, { onRequestClose: close });
  installPeek(modal, modal.querySelector<HTMLButtonElement>('[data-action="peek"]')!);
}
